/**
 * 视频交互Modal组件
 *
 * 功能：
 * 1. 全屏显示视频，保持16:9比例
 * 2. 跟踪模式 - 点击视频设置跟踪目标点，按track_mode分层控制
 * 3. 检测模式 - 检测参数配置 + ROI区域框选
 * 4. 查看模式 - 仅查看视频
 * 5. 发送控制指令 - 将设置应用到设备
 *
 * 协议版本: V2.0 (v1.0.0_20260105)
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Modal,
  Row,
  Col,
  Card,
  Radio,
  Space,
  Button,
  InputNumber,
  Select,
  Typography,
  message,
  Divider,
  Switch,
  Slider,
  Checkbox,
} from 'antd'
import {
  DeleteOutlined,
  UndoOutlined,
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  SendOutlined,
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  AimOutlined,
} from '@ant-design/icons'
import useWebSocket from '@/hooks/useWebSocket'

const { Text } = Typography

// 组件属性接口
interface VideoInteractionModalProps {
  visible: boolean
  onClose: () => void
  /** 共享的播放器宿主 DOM（来自 Monitor），打开时挪到 Modal 内、关闭时挪回 */
  playerHost: HTMLDivElement | null
  deviceId: string
  streamType: 'vis' | 'ir'
}

// 点坐标接口
interface Point {
  x: number
  y: number
}

// 矩形区域接口
interface Rect {
  x1: number
  y1: number
  x2: number
  y2: number
}

// 交互模式类型（工作模式）
type InteractionMode = 'view' | 'track' | 'detect'

// 拖拽操作类型
type DragOperation = 'none' | 'resize' | 'move-roi' | 'move-track' | 'create-roi'

// 调整手柄类型
type ResizeHandle = 'tl' | 'tr' | 'bl' | 'br' | 't' | 'b' | 'l' | 'r' | null

// 视频分辨率常量
const VIDEO_WIDTH = 1920
const VIDEO_HEIGHT = 1080

function VideoInteractionModal({
  visible,
  onClose,
  playerHost,
  deviceId,
  streamType,
}: VideoInteractionModalProps) {
  const { sendControl } = useWebSocket()

  // 根据 streamType 确定 confKey
  const confKey = streamType === 'vis' ? 'vi_conf' : 'ir_conf'

  // Canvas引用
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  // 播放器宿主在 Modal 内的挂载点（用 callback ref 保证 antd Modal 异步挂载完毕后立即搬运）
  const playerSlotRef = useRef<HTMLDivElement | null>(null)
  const setPlayerSlotRef = useCallback((node: HTMLDivElement | null) => {
    playerSlotRef.current = node
    if (node && visible && playerHost && playerHost.parentNode !== node) {
      playerHost.classList.add('host-in-modal')
      node.appendChild(playerHost)
    }
  }, [visible, playerHost])

  // Modal 拖拽偏移（相对默认居中位置）
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 })
  const dragStartPosRef = useRef<{ mouseX: number; mouseY: number; offX: number; offY: number } | null>(null)

  // 窗口尺寸状态（仅窗口模式生效）
  const DEFAULT_WINDOW_SIZE = { width: 1200, height: 720 }
  const MIN_WINDOW_SIZE = { width: 720, height: 480 }
  const [isMaximized, setIsMaximized] = useState(false)
  const [windowSize, setWindowSize] = useState(DEFAULT_WINDOW_SIZE)

  // resize 方向类型
  type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
  const resizeStartRef = useRef<{
    dir: ResizeDir
    mouseX: number
    mouseY: number
    w: number
    h: number
    offX: number
    offY: number
  } | null>(null)

  const handleTitleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isMaximized) return // 最大化时不允许拖动
    if (e.button !== 0) return
    const target = e.target as HTMLElement
    if (target.closest('button, a, input, .ant-btn')) return
    dragStartPosRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      offX: modalOffset.x,
      offY: modalOffset.y,
    }
    e.preventDefault()
  }, [modalOffset, isMaximized])

  const startResize = useCallback((dir: ResizeDir) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMaximized) return
    if (e.button !== 0) return
    resizeStartRef.current = {
      dir,
      mouseX: e.clientX,
      mouseY: e.clientY,
      w: windowSize.width,
      h: windowSize.height,
      offX: modalOffset.x,
      offY: modalOffset.y,
    }
    e.preventDefault()
    e.stopPropagation()
  }, [isMaximized, windowSize, modalOffset])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const dragStart = dragStartPosRef.current
      if (dragStart) {
        setModalOffset({
          x: dragStart.offX + (e.clientX - dragStart.mouseX),
          y: dragStart.offY + (e.clientY - dragStart.mouseY),
        })
      }
      const resizeStart = resizeStartRef.current
      if (resizeStart) {
        const dx = e.clientX - resizeStart.mouseX
        const dy = e.clientY - resizeStart.mouseY
        let newW = resizeStart.w
        let newH = resizeStart.h
        let newOffX = resizeStart.offX
        let newOffY = resizeStart.offY
        const dir = resizeStart.dir

        if (dir.includes('e')) {
          newW = Math.max(MIN_WINDOW_SIZE.width, resizeStart.w + dx)
        }
        if (dir.includes('s')) {
          newH = Math.max(MIN_WINDOW_SIZE.height, resizeStart.h + dy)
        }
        if (dir.includes('w')) {
          const maxDx = resizeStart.w - MIN_WINDOW_SIZE.width
          const limitedDx = Math.min(dx, maxDx)
          newW = resizeStart.w - limitedDx
          newOffX = resizeStart.offX + limitedDx
        }
        if (dir.includes('n')) {
          const maxDy = resizeStart.h - MIN_WINDOW_SIZE.height
          const limitedDy = Math.min(dy, maxDy)
          newH = resizeStart.h - limitedDy
          newOffY = resizeStart.offY + limitedDy
        }

        setWindowSize({ width: newW, height: newH })
        if (dir.includes('w') || dir.includes('n')) {
          setModalOffset({ x: newOffX, y: newOffY })
        }
      }
    }
    const onMouseUp = () => {
      dragStartPosRef.current = null
      resizeStartRef.current = null
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  // Modal 关闭时重置位置和最大化状态（默认窗口模式）
  useEffect(() => {
    if (!visible) {
      setModalOffset({ x: 0, y: 0 })
      setIsMaximized(false)
      setWindowSize(DEFAULT_WINDOW_SIZE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // 交互模式状态
  const [interactionMode, setInteractionMode] = useState<InteractionMode>('view')

  // 跟踪点状态
  const [trackPoint, setTrackPoint] = useState<[number, number] | null>(null)

  // ROI区域状态
  const [roiRect, setRoiRect] = useState<Rect | null>(null)

  // 拖拽状态（使用ref以避免闭包问题）
  const isDraggingRef = useRef(false)
  const dragOperationRef = useRef<DragOperation>('none')
  const dragStartVideoCoordRef = useRef<Point | null>(null)
  const originalRoiRectRef = useRef<Rect | null>(null)
  const originalTrackPointRef = useRef<[number, number] | null>(null)
  const resizeHandleRef = useRef<ResizeHandle>(null)

  // 拖拽状态（用于UI显示）
  const [isDragging, setIsDragging] = useState(false)
  const [dragOperation, setDragOperation] = useState<DragOperation>('none')
  const [, setDragStart] = useState<Point | null>(null)
  const [, setDragStartVideoCoord] = useState<Point | null>(null)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null)
  const [, setOriginalRoiRect] = useState<Rect | null>(null)
  const [, setOriginalTrackPoint] = useState<[number, number] | null>(null)
  // 鼠标悬停状态
  const [isHoveringTrackPoint, setIsHoveringTrackPoint] = useState(false)
  const [isHoveringROI, setIsHoveringROI] = useState(false)

  // ============================================================
  // 跟踪控制参数
  // ============================================================
  const [trackMode, setTrackMode] = useState(0) // 0:关闭, 1:像素点, 2:识别, 3:目标ID
  const [trackId, setTrackId] = useState<number>(1)
  const [gateSize, setGateSize] = useState(0) // 0:自适应
  const [gateStep, setGateStep] = useState(5) // 波门移动步长（像素）
  const [trackPlot, setTrackPlot] = useState(1) // 绘制跟踪结果: 0=关, 1=开
  const [centerCrossStep, setCenterCrossStep] = useState(1)
  const [centerCrossPoint, setCenterCrossPoint] = useState<[number, number]>([960, 540])

  // ============================================================
  // 检测控制参数
  // ============================================================
  const [detectEn, setDetectEn] = useState(1) // 检测模式: 0=关闭, 1=切片检测, 2=全图检测
  const [detectType, setDetectType] = useState<string[]>(['drone']) // 检测类型
  const [detectConf, setDetectConf] = useState(50) // 置信度 10-100
  const [smallArea, setSmallArea] = useState(100) // 小于该像素面积的目标直接过滤
  const [bigArea, setBigArea] = useState(100000) // 大于该像素面积的目标直接过滤
  const [modelName, setModelName] = useState('NORMAL') // AI模型: NORMAL=大小目标混训(默认), SLICE_SMALL=仅小目标
  const [detectPlot, setDetectPlot] = useState(1) // 绘制检测结果
  const [roiMode, setRoiMode] = useState(0) // 0:全图, 1:窗口选取

  // 临时预览ROI（用于创建时的实时预览）
  const [previewRoiRect, setPreviewRoiRect] = useState<Rect | null>(null)

  /**
   * 清空Canvas
   */
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }, [])

  /**
   * 将视频坐标转换为Canvas坐标
   */
  const videoToCanvasCoords = useCallback((videoX: number, videoY: number): Point => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    return {
      x: (videoX / VIDEO_WIDTH) * canvas.width,
      y: (videoY / VIDEO_HEIGHT) * canvas.height,
    }
  }, [])

  /**
   * 将Canvas坐标转换为视频坐标（带边界限制）
   */
  const canvasToVideoCoords = useCallback((canvasX: number, canvasY: number): Point => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const clampedX = Math.max(0, Math.min(canvasX, canvas.width))
    const clampedY = Math.max(0, Math.min(canvasY, canvas.height))

    return {
      x: Math.round((clampedX / canvas.width) * VIDEO_WIDTH),
      y: Math.round((clampedY / canvas.height) * VIDEO_HEIGHT),
    }
  }, [])

  /**
   * 限制视频坐标在画面范围内
   */
  const clampVideoCoords = useCallback((x: number, y: number): Point => {
    return {
      x: Math.max(0, Math.min(x, VIDEO_WIDTH)),
      y: Math.max(0, Math.min(y, VIDEO_HEIGHT)),
    }
  }, [])

  /**
   * 绘制跟踪点十字准星
   */
  const drawTrackPoint = useCallback(
    (videoX: number, videoY: number, isHovered = false) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const { x: canvasX, y: canvasY } = videoToCanvasCoords(videoX, videoY)

      ctx.strokeStyle = isHovered ? '#73d13d' : '#52c41a'
      ctx.lineWidth = isHovered ? 3 : 2
      ctx.beginPath()

      ctx.moveTo(canvasX - 15, canvasY)
      ctx.lineTo(canvasX + 15, canvasY)

      ctx.moveTo(canvasX, canvasY - 15)
      ctx.lineTo(canvasX, canvasY + 15)

      ctx.stroke()

      ctx.beginPath()
      ctx.arc(canvasX, canvasY, isHovered ? 10 : 8, 0, 2 * Math.PI)
      ctx.stroke()

      ctx.fillStyle = '#52c41a'
      ctx.font = '12px Arial'
      ctx.fillText(`(${videoX}, ${videoY})`, canvasX + 12, canvasY - 12)
    },
    [videoToCanvasCoords]
  )

  /**
   * 绘制调整手柄
   */
  const drawResizeHandles = useCallback(
    (x1: number, y1: number, x2: number, y2: number) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const handleSize = 8

      const handles = [
        { x: x1, y: y1 },
        { x: x2, y: y1 },
        { x: x1, y: y2 },
        { x: x2, y: y2 },
        { x: (x1 + x2) / 2, y: y1 },
        { x: (x1 + x2) / 2, y: y2 },
        { x: x1, y: (y1 + y2) / 2 },
        { x: x2, y: (y1 + y2) / 2 },
      ]

      ctx.fillStyle = '#fff'
      ctx.strokeStyle = '#faad14'
      ctx.lineWidth = 2

      handles.forEach((handle) => {
        ctx.fillRect(
          handle.x - handleSize / 2,
          handle.y - handleSize / 2,
          handleSize,
          handleSize
        )
        ctx.strokeRect(
          handle.x - handleSize / 2,
          handle.y - handleSize / 2,
          handleSize,
          handleSize
        )
      })
    },
    []
  )

  /**
   * 绘制ROI矩形框
   */
  const drawROI = useCallback(
    (rect: Rect, isPreview = false) => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const { x: x1, y: y1 } = videoToCanvasCoords(rect.x1, rect.y1)
      const { x: x2, y: y2 } = videoToCanvasCoords(rect.x2, rect.y2)

      ctx.strokeStyle = '#faad14'
      ctx.lineWidth = 2
      ctx.setLineDash([5, 5])
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)
      ctx.setLineDash([])

      ctx.fillStyle = 'rgba(250, 173, 20, 0.1)'
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1)

      ctx.fillStyle = '#faad14'
      ctx.font = '12px Arial'
      ctx.fillText(
        `[${rect.x1}, ${rect.y1}, ${rect.x2}, ${rect.y2}]`,
        x1,
        y1 - 5
      )

      if (!isPreview) {
        drawResizeHandles(x1, y1, x2, y2)
      }
    },
    [videoToCanvasCoords, drawResizeHandles]
  )

  /**
   * 重绘所有标记（根据工作模式显示对应内容）
   */
  const redrawAll = useCallback(() => {
    clearCanvas()

    if (interactionMode === 'view') {
      return
    }

    // 跟踪模式：只在trackMode=1时显示跟踪点
    if (interactionMode === 'track' && trackPoint && trackMode === 1) {
      drawTrackPoint(trackPoint[0], trackPoint[1])
      return
    }

    // 检测模式：只在roiMode=1时显示ROI区域
    if (interactionMode === 'detect' && roiMode === 1) {
      const rectToDraw = previewRoiRect || roiRect
      if (rectToDraw) {
        drawROI(rectToDraw, !!previewRoiRect)
      }
    }
  }, [clearCanvas, interactionMode, trackPoint, trackMode, roiRect, roiMode, previewRoiRect, drawTrackPoint, drawROI])

  /**
   * 更新Canvas尺寸
   */
  const updateCanvasSize = useCallback(() => {
    const container = videoContainerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const { width, height } = container.getBoundingClientRect()
    canvas.width = width
    canvas.height = height

    redrawAll()
  }, [redrawAll])

  /**
   * 检查是否点击在跟踪点上
   */
  const isClickOnTrackPoint = useCallback(
    (canvasX: number, canvasY: number): boolean => {
      if (!trackPoint) return false

      const { x, y } = videoToCanvasCoords(trackPoint[0], trackPoint[1])
      const distance = Math.sqrt(Math.pow(canvasX - x, 2) + Math.pow(canvasY - y, 2))

      return distance < 15
    },
    [trackPoint, videoToCanvasCoords]
  )

  /**
   * 检查是否点击在ROI框内
   */
  const isClickInsideROI = useCallback(
    (canvasX: number, canvasY: number): boolean => {
      if (!roiRect) return false

      const { x: x1, y: y1 } = videoToCanvasCoords(roiRect.x1, roiRect.y1)
      const { x: x2, y: y2 } = videoToCanvasCoords(roiRect.x2, roiRect.y2)

      const margin = 12
      return (
        canvasX > x1 + margin &&
        canvasX < x2 - margin &&
        canvasY > y1 + margin &&
        canvasY < y2 - margin
      )
    },
    [roiRect, videoToCanvasCoords]
  )

  /**
   * 获取鼠标点击的调整手柄
   */
  const getResizeHandle = useCallback(
    (canvasX: number, canvasY: number): ResizeHandle => {
      if (!roiRect) return null

      const { x: x1, y: y1 } = videoToCanvasCoords(roiRect.x1, roiRect.y1)
      const { x: x2, y: y2 } = videoToCanvasCoords(roiRect.x2, roiRect.y2)

      const handleSize = 12

      const handles: Array<{ name: ResizeHandle; x: number; y: number }> = [
        { name: 'tl', x: x1, y: y1 },
        { name: 'tr', x: x2, y: y1 },
        { name: 'bl', x: x1, y: y2 },
        { name: 'br', x: x2, y: y2 },
        { name: 't', x: (x1 + x2) / 2, y: y1 },
        { name: 'b', x: (x1 + x2) / 2, y: y2 },
        { name: 'l', x: x1, y: (y1 + y2) / 2 },
        { name: 'r', x: x2, y: (y1 + y2) / 2 },
      ]

      for (const handle of handles) {
        if (
          Math.abs(canvasX - handle.x) < handleSize &&
          Math.abs(canvasY - handle.y) < handleSize
        ) {
          return handle.name
        }
      }

      return null
    },
    [roiRect, videoToCanvasCoords]
  )

  /**
   * Canvas 是否可交互
   */
  const canvasInteractive = useCallback((): boolean => {
    if (interactionMode === 'view') return false
    if (interactionMode === 'track') return trackMode === 1
    if (interactionMode === 'detect') return roiMode === 1
    return false
  }, [interactionMode, trackMode, roiMode])

  /**
   * 获取光标样式
   */
  const getCursor = useCallback((): string => {
    if (!canvasInteractive()) return 'default'

    if (interactionMode === 'track') {
      if (isDragging && dragOperation === 'move-track') {
        return 'grabbing'
      }
      if (isHoveringTrackPoint) {
        return 'grab'
      }
      return 'crosshair'
    }

    if (interactionMode === 'detect') {
      if (isDragging && dragOperation === 'move-roi') {
        return 'grabbing'
      }
      if (resizeHandle) {
        const cursorMap: Record<string, string> = {
          tl: 'nwse-resize',
          tr: 'nesw-resize',
          bl: 'nesw-resize',
          br: 'nwse-resize',
          t: 'ns-resize',
          b: 'ns-resize',
          l: 'ew-resize',
          r: 'ew-resize',
        }
        return cursorMap[resizeHandle] || 'crosshair'
      }
      if (isHoveringROI) {
        return 'grab'
      }
      return 'crosshair'
    }
    return 'default'
  }, [interactionMode, canvasInteractive, resizeHandle, isDragging, dragOperation, isHoveringTrackPoint, isHoveringROI])

  /**
   * 跟踪模式: 点击设置跟踪点后立即发送指令
   */
  const sendTrackImmediate = useCallback((pt: [number, number]) => {
    if (!deviceId) return

    const trackConf: Record<string, unknown> = {
      track_mode: 1,
      track_pt: pt,
      track_plot: trackPlot,
      gate_size: gateSize,
    }

    sendControl(deviceId, { [confKey]: { track_conf: trackConf } })
    message.success('已发送跟踪指令')
  }, [deviceId, confKey, trackPlot, gateSize, sendControl])

  /**
   * 处理右键菜单（删除跟踪点或ROI）
   */
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()

      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top

      // 跟踪模式：右键删除跟踪点
      if (interactionMode === 'track' && trackPoint) {
        if (isClickOnTrackPoint(canvasX, canvasY)) {
          setTrackPoint(null)
          setIsHoveringTrackPoint(false)
          message.info('跟踪点已删除')
          return
        }
      }

      // 检测模式：右键删除ROI
      if (interactionMode === 'detect' && roiRect) {
        const { x: x1, y: y1 } = videoToCanvasCoords(roiRect.x1, roiRect.y1)
        const { x: x2, y: y2 } = videoToCanvasCoords(roiRect.x2, roiRect.y2)

        if (canvasX >= x1 && canvasX <= x2 && canvasY >= y1 && canvasY <= y2) {
          setRoiRect(null)
          message.info('检测区域已删除')
        }
      }
    },
    [interactionMode, trackPoint, roiRect, isClickOnTrackPoint, videoToCanvasCoords]
  )

  /**
   * 处理鼠标按下事件
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (e.button === 2) return
      if (!canvasInteractive()) return

      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top
      const videoCoord = canvasToVideoCoords(canvasX, canvasY)

      // 跟踪模式 (仅 trackMode=1)
      if (interactionMode === 'track' && trackMode === 1) {
        if (trackPoint && isClickOnTrackPoint(canvasX, canvasY)) {
          // 开始拖动跟踪点
          isDraggingRef.current = true
          dragOperationRef.current = 'move-track'
          originalTrackPointRef.current = [...trackPoint]
          setIsDragging(true)
          setDragOperation('move-track')
          setDragStart({ x: canvasX, y: canvasY })
          setOriginalTrackPoint([...trackPoint])
        } else {
          // 设置新的跟踪点并立即发送
          const newPt: [number, number] = [videoCoord.x, videoCoord.y]
          setTrackPoint(newPt)
          sendTrackImmediate(newPt)
        }
        return
      }

      // 检测模式 (仅 roiMode=1)
      if (interactionMode === 'detect' && roiMode === 1) {
        // 检查调整手柄
        const handle = getResizeHandle(canvasX, canvasY)
        if (handle && roiRect) {
          isDraggingRef.current = true
          dragOperationRef.current = 'resize'
          resizeHandleRef.current = handle
          dragStartVideoCoordRef.current = videoCoord
          originalRoiRectRef.current = { ...roiRect }
          setResizeHandle(handle)
          setIsDragging(true)
          setDragOperation('resize')
          setDragStart({ x: canvasX, y: canvasY })
          setDragStartVideoCoord(videoCoord)
          setOriginalRoiRect({ ...roiRect })
          return
        }

        // 检查ROI框内拖动
        if (roiRect && isClickInsideROI(canvasX, canvasY)) {
          isDraggingRef.current = true
          dragOperationRef.current = 'move-roi'
          dragStartVideoCoordRef.current = videoCoord
          originalRoiRectRef.current = { ...roiRect }
          setIsDragging(true)
          setDragOperation('move-roi')
          setDragStart({ x: canvasX, y: canvasY })
          setDragStartVideoCoord(videoCoord)
          setOriginalRoiRect({ ...roiRect })
          return
        }

        // 创建新的ROI
        isDraggingRef.current = true
        dragOperationRef.current = 'create-roi'
        dragStartVideoCoordRef.current = videoCoord
        setIsDragging(true)
        setDragOperation('create-roi')
        setDragStart({ x: canvasX, y: canvasY })
        setDragStartVideoCoord(videoCoord)
        setRoiRect(null)
        setPreviewRoiRect(null)
      }
    },
    [
      interactionMode,
      trackMode,
      trackPoint,
      roiRect,
      roiMode,
      canvasInteractive,
      isClickOnTrackPoint,
      isClickInsideROI,
      getResizeHandle,
      canvasToVideoCoords,
      sendTrackImmediate,
    ]
  )

  /**
   * 处理鼠标移动事件
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = Math.max(0, Math.min(e.clientX - rect.left, canvas.width))
      const canvasY = Math.max(0, Math.min(e.clientY - rect.top, canvas.height))

      // 检测模式: 更新手柄和悬停状态
      if (interactionMode === 'detect' && roiMode === 1 && !isDraggingRef.current) {
        const handle = getResizeHandle(canvasX, canvasY)
        setResizeHandle(handle)
        resizeHandleRef.current = handle
        if (!handle) {
          setIsHoveringROI(isClickInsideROI(canvasX, canvasY))
        } else {
          setIsHoveringROI(false)
        }
      }

      // 跟踪模式: 检测跟踪点悬停
      if (interactionMode === 'track' && trackMode === 1 && !isDraggingRef.current) {
        setIsHoveringTrackPoint(isClickOnTrackPoint(canvasX, canvasY))
      }

      if (!isDraggingRef.current) return

      const videoCoord = canvasToVideoCoords(canvasX, canvasY)
      const currentDragOp = dragOperationRef.current

      // 拖动跟踪点
      if (currentDragOp === 'move-track' && originalTrackPointRef.current) {
        const clampedCoord = clampVideoCoords(videoCoord.x, videoCoord.y)
        setTrackPoint([clampedCoord.x, clampedCoord.y])
        return
      }

      // 调整ROI大小
      if (currentDragOp === 'resize' && resizeHandleRef.current && originalRoiRectRef.current) {
        const origRect = originalRoiRectRef.current
        const newRect = { ...origRect }

        switch (resizeHandleRef.current) {
          case 'tl':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'tr':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'bl':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 'br':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 't':
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'b':
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 'l':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            break
          case 'r':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            break
        }

        newRect.x1 = Math.max(0, Math.min(newRect.x1, VIDEO_WIDTH))
        newRect.y1 = Math.max(0, Math.min(newRect.y1, VIDEO_HEIGHT))
        newRect.x2 = Math.max(0, Math.min(newRect.x2, VIDEO_WIDTH))
        newRect.y2 = Math.max(0, Math.min(newRect.y2, VIDEO_HEIGHT))

        setRoiRect(newRect)
        return
      }

      // 移动ROI
      if (currentDragOp === 'move-roi' && originalRoiRectRef.current && dragStartVideoCoordRef.current) {
        const origRect = originalRoiRectRef.current
        const startCoord = dragStartVideoCoordRef.current
        const deltaX = videoCoord.x - startCoord.x
        const deltaY = videoCoord.y - startCoord.y

        let newX1 = origRect.x1 + deltaX
        let newY1 = origRect.y1 + deltaY
        let newX2 = origRect.x2 + deltaX
        let newY2 = origRect.y2 + deltaY

        const roiWidth = origRect.x2 - origRect.x1
        const roiHeight = origRect.y2 - origRect.y1

        if (newX1 < 0) { newX1 = 0; newX2 = roiWidth }
        if (newY1 < 0) { newY1 = 0; newY2 = roiHeight }
        if (newX2 > VIDEO_WIDTH) { newX2 = VIDEO_WIDTH; newX1 = VIDEO_WIDTH - roiWidth }
        if (newY2 > VIDEO_HEIGHT) { newY2 = VIDEO_HEIGHT; newY1 = VIDEO_HEIGHT - roiHeight }

        setRoiRect({ x1: newX1, y1: newY1, x2: newX2, y2: newY2 })
        return
      }

      // 创建新ROI
      if (currentDragOp === 'create-roi' && dragStartVideoCoordRef.current) {
        const startCoord = dragStartVideoCoordRef.current
        const newPreviewRect: Rect = {
          x1: Math.max(0, Math.min(startCoord.x, videoCoord.x)),
          y1: Math.max(0, Math.min(startCoord.y, videoCoord.y)),
          x2: Math.min(VIDEO_WIDTH, Math.max(startCoord.x, videoCoord.x)),
          y2: Math.min(VIDEO_HEIGHT, Math.max(startCoord.y, videoCoord.y)),
        }
        setPreviewRoiRect(newPreviewRect)
      }
    },
    [
      interactionMode,
      trackMode,
      roiMode,
      canvasToVideoCoords,
      clampVideoCoords,
      getResizeHandle,
      isClickOnTrackPoint,
      isClickInsideROI,
    ]
  )

  /**
   * 重置拖拽状态
   */
  const resetDragState = useCallback(() => {
    isDraggingRef.current = false
    dragOperationRef.current = 'none'
    dragStartVideoCoordRef.current = null
    originalRoiRectRef.current = null
    originalTrackPointRef.current = null

    setIsDragging(false)
    setDragOperation('none')
    setDragStart(null)
    setDragStartVideoCoord(null)
    setOriginalRoiRect(null)
    setOriginalTrackPoint(null)
  }, [])

  /**
   * 完成ROI创建
   */
  const finalizeRoiCreation = useCallback(() => {
    if (previewRoiRect) {
      if (
        Math.abs(previewRoiRect.x2 - previewRoiRect.x1) > 10 &&
        Math.abs(previewRoiRect.y2 - previewRoiRect.y1) > 10
      ) {
        setRoiRect(previewRoiRect)
      }
      setPreviewRoiRect(null)
    }
  }, [previewRoiRect])

  /**
   * 处理鼠标释放事件
   */
  const handleMouseUp = useCallback(
    () => {
      if (!isDraggingRef.current) return

      if (dragOperationRef.current === 'create-roi') {
        finalizeRoiCreation()
      }

      // 拖动跟踪点后立即发送
      if (dragOperationRef.current === 'move-track' && trackPoint) {
        sendTrackImmediate(trackPoint)
      }

      resetDragState()
    },
    [finalizeRoiCreation, resetDragState, trackPoint, sendTrackImmediate]
  )

  /**
   * 处理document级别事件
   */
  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (!isDraggingRef.current) return

      if (dragOperationRef.current === 'create-roi' && previewRoiRect) {
        if (
          Math.abs(previewRoiRect.x2 - previewRoiRect.x1) > 10 &&
          Math.abs(previewRoiRect.y2 - previewRoiRect.y1) > 10
        ) {
          setRoiRect(previewRoiRect)
        }
        setPreviewRoiRect(null)
      }

      resetDragState()
    }

    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return

      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = Math.max(0, Math.min(e.clientX - rect.left, canvas.width))
      const canvasY = Math.max(0, Math.min(e.clientY - rect.top, canvas.height))

      const videoCoord = canvasToVideoCoords(canvasX, canvasY)
      const currentDragOp = dragOperationRef.current

      if (currentDragOp === 'move-track' && originalTrackPointRef.current) {
        const clampedCoord = clampVideoCoords(videoCoord.x, videoCoord.y)
        setTrackPoint([clampedCoord.x, clampedCoord.y])
        return
      }

      if (currentDragOp === 'resize' && resizeHandleRef.current && originalRoiRectRef.current) {
        const origRect = originalRoiRectRef.current
        const newRect = { ...origRect }

        switch (resizeHandleRef.current) {
          case 'tl':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'tr':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'bl':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 'br':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 't':
            newRect.y1 = Math.min(videoCoord.y, origRect.y2 - 10)
            break
          case 'b':
            newRect.y2 = Math.max(videoCoord.y, origRect.y1 + 10)
            break
          case 'l':
            newRect.x1 = Math.min(videoCoord.x, origRect.x2 - 10)
            break
          case 'r':
            newRect.x2 = Math.max(videoCoord.x, origRect.x1 + 10)
            break
        }

        newRect.x1 = Math.max(0, Math.min(newRect.x1, VIDEO_WIDTH))
        newRect.y1 = Math.max(0, Math.min(newRect.y1, VIDEO_HEIGHT))
        newRect.x2 = Math.max(0, Math.min(newRect.x2, VIDEO_WIDTH))
        newRect.y2 = Math.max(0, Math.min(newRect.y2, VIDEO_HEIGHT))

        setRoiRect(newRect)
        return
      }

      if (currentDragOp === 'move-roi' && originalRoiRectRef.current && dragStartVideoCoordRef.current) {
        const origRect = originalRoiRectRef.current
        const startCoord = dragStartVideoCoordRef.current
        const deltaX = videoCoord.x - startCoord.x
        const deltaY = videoCoord.y - startCoord.y

        let newX1 = origRect.x1 + deltaX
        let newY1 = origRect.y1 + deltaY
        let newX2 = origRect.x2 + deltaX
        let newY2 = origRect.y2 + deltaY

        const roiWidth = origRect.x2 - origRect.x1
        const roiHeight = origRect.y2 - origRect.y1

        if (newX1 < 0) { newX1 = 0; newX2 = roiWidth }
        if (newY1 < 0) { newY1 = 0; newY2 = roiHeight }
        if (newX2 > VIDEO_WIDTH) { newX2 = VIDEO_WIDTH; newX1 = VIDEO_WIDTH - roiWidth }
        if (newY2 > VIDEO_HEIGHT) { newY2 = VIDEO_HEIGHT; newY1 = VIDEO_HEIGHT - roiHeight }

        setRoiRect({ x1: newX1, y1: newY1, x2: newX2, y2: newY2 })
        return
      }

      if (currentDragOp === 'create-roi' && dragStartVideoCoordRef.current) {
        const startCoord = dragStartVideoCoordRef.current
        const newPreviewRect: Rect = {
          x1: Math.max(0, Math.min(startCoord.x, videoCoord.x)),
          y1: Math.max(0, Math.min(startCoord.y, videoCoord.y)),
          x2: Math.min(VIDEO_WIDTH, Math.max(startCoord.x, videoCoord.x)),
          y2: Math.min(VIDEO_HEIGHT, Math.max(startCoord.y, videoCoord.y)),
        }
        setPreviewRoiRect(newPreviewRect)
      }
    }

    document.addEventListener('mouseup', handleDocumentMouseUp)
    document.addEventListener('mousemove', handleDocumentMouseMove)

    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp)
      document.removeEventListener('mousemove', handleDocumentMouseMove)
    }
  }, [previewRoiRect, resetDragState, canvasToVideoCoords, clampVideoCoords])

  /**
   * 设置预设ROI区域
   */
  const setPresetROI = useCallback(
    (preset: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'full') => {
      let rect: Rect

      switch (preset) {
        case 'center':
          rect = { x1: 480, y1: 270, x2: 1440, y2: 810 }
          break
        case 'top':
          rect = { x1: 0, y1: 0, x2: 1920, y2: 540 }
          break
        case 'bottom':
          rect = { x1: 0, y1: 540, x2: 1920, y2: 1080 }
          break
        case 'left':
          rect = { x1: 0, y1: 0, x2: 960, y2: 1080 }
          break
        case 'right':
          rect = { x1: 960, y1: 0, x2: 1920, y2: 1080 }
          break
        case 'full':
        default:
          rect = { x1: 0, y1: 0, x2: 1920, y2: 1080 }
          break
      }

      setRoiRect(rect)
    },
    []
  )

  /**
   * 发送波门移动指令
   * V2.0协议: gate_dir 上=0, 下=1, 左=2, 右=3
   */
  const handleGateMove = useCallback((direction: number) => {
    if (!deviceId) {
      message.warning('未选择设备')
      return
    }

    const trackConf: Record<string, unknown> = {
      gate_dir: direction,
      gate_step: gateStep,
    }

    sendControl(deviceId, { [confKey]: { track_conf: trackConf } })

    const directionNames: Record<number, string> = {
      0: '上',
      1: '下',
      2: '左',
      3: '右',
    }
    message.success(`波门向${directionNames[direction]}移动 ${gateStep} 像素`)
  }, [deviceId, confKey, gateStep, sendControl])

  /**
   * 移动当前视频源的中心十字，每次指令由设备端执行一次1像素移动。
   * 0=上, 1=下, 2=左, 3=右。
   */
  const handleCenterCrossMove = useCallback((direction: number) => {
    if (!deviceId) {
      message.warning('未选择设备')
      return
    }

    sendControl(deviceId, { [confKey]: { track_conf: { center_cross_dir: direction, center_cross_step: centerCrossStep } } })
    setCenterCrossPoint(([x, y]) => [
      Math.max(0, Math.min(1919, x + (direction === 2 ? -centerCrossStep : direction === 3 ? centerCrossStep : 0))),
      Math.max(0, Math.min(1079, y + (direction === 0 ? -centerCrossStep : direction === 1 ? centerCrossStep : 0))),
    ])
  }, [deviceId, confKey, sendControl, centerCrossStep])

  const saveCenterCross = useCallback(() => {
    if (!deviceId) return message.warning('未选择设备')
    sendControl(deviceId, { [confKey]: { track_conf: { center_cross_point: { x: centerCrossPoint[0], y: centerCrossPoint[1] }, center_cross_save: true } } })
    message.success('中心十字坐标已保存')
  }, [deviceId, confKey, sendControl, centerCrossPoint])

  /**
   * 跟踪模式: "发送"按钮处理
   */
  const handleTrackSend = useCallback(() => {
    if (!deviceId) {
      message.warning('未选择设备')
      return
    }

    const params: Record<string, unknown> = { track_mode: trackMode }

    if (trackMode === 1) {
      params.gate_size = gateSize
      if (trackPoint) {
        params.track_pt = trackPoint
        params.track_plot = trackPlot
      }
    } else if (trackMode === 3) {
      params.track_id = trackId
    }

    sendControl(deviceId, { [confKey]: { track_conf: params } })
    message.success('跟踪指令已发送')
  }, [deviceId, confKey, trackMode, trackPoint, gateSize, trackPlot, trackId, sendControl])

  /**
   * 检测模式: "发送"按钮处理
   */
  const handleDetectSend = useCallback(() => {
    if (!deviceId) {
      message.warning('未选择设备')
      return
    }

    // 构建 detect_type 对象
    const typeObj: Record<string, number> = {}
    detectType.forEach(t => { typeObj[t] = 1 })

    const detectConfData: Record<string, unknown> = {
      detect_en: detectEn,
      detect_type: typeObj,
      conf: detectConf,
      small_area: smallArea,
      big_area: bigArea,
      model_name: modelName,
      detect_plot: detectPlot,
      roi_mode: roiMode,
    }

    // 仅 roi_mode=1 时包含 roi_rect
    if (roiMode === 1 && roiRect) {
      detectConfData.roi_rect = [roiRect.x1, roiRect.y1, roiRect.x2, roiRect.y2]
    }

    sendControl(deviceId, { [confKey]: { detect_conf: detectConfData } })
    message.success('检测指令已发送')
  }, [deviceId, confKey, detectEn, detectType, detectConf, smallArea, bigArea, modelName, detectPlot, roiMode, roiRect, sendControl])

  /**
   * track_plot 修改后立即发送
   */
  const handleTrackPlotChange = useCallback((val: boolean) => {
    const newVal = val ? 1 : 0
    setTrackPlot(newVal)
    if (deviceId) {
      sendControl(deviceId, { [confKey]: { track_conf: { track_plot: newVal } } })
    }
  }, [deviceId, confKey, sendControl])

  /**
   * 重置所有设置
   */
  const handleReset = useCallback(() => {
    setTrackPoint(null)
    setRoiRect(null)
    setInteractionMode('view')
    setTrackMode(0)
    setTrackId(1)
    setGateSize(0)
    setGateStep(5)
    setTrackPlot(1)
    setDetectEn(1)
    setDetectType(['drone'])
    setDetectConf(50)
    setSmallArea(100)
    setBigArea(100000)
    setModelName('NORMAL')
    setDetectPlot(1)
    setRoiMode(0)
    clearCanvas()
  }, [clearCanvas])

  // 容器检测和ResizeObserver
  useEffect(() => {
    if (!visible) return

    let resizeObserver: ResizeObserver | null = null
    let pollTimer: ReturnType<typeof setTimeout> | null = null
    let pollCount = 0
    const maxPolls = 30

    const pollForContainer = () => {
      const container = videoContainerRef.current

      if (!container) {
        pollCount++
        if (pollCount < maxPolls) {
          pollTimer = setTimeout(pollForContainer, 100)
        }
        return
      }

      const rect = container.getBoundingClientRect()

      if (rect.width > 100 && rect.height > 50) {
        updateCanvasSize()
      }

      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect
          if (width > 100 && height > 50) {
            updateCanvasSize()
          }
        }
      })
      resizeObserver.observe(container)
    }

    pollTimer = setTimeout(pollForContainer, 50)

    window.addEventListener('resize', updateCanvasSize)

    return () => {
      if (pollTimer) clearTimeout(pollTimer)
      if (resizeObserver) resizeObserver.disconnect()
      window.removeEventListener('resize', updateCanvasSize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  // 重绘标记
  useEffect(() => {
    if (visible) {
      redrawAll()
    }
  }, [visible, trackPoint, roiRect, previewRoiRect, redrawAll])

  // ============================================================
  // 行内样式复用
  // ============================================================
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  }
  const labelStyle: React.CSSProperties = { fontSize: 11, color: '#595959', flexShrink: 0 }

  // 跟踪模式下其他选项是否禁用
  const trackOptionsDisabled = trackMode === 0 || trackMode === 2

  return (
    <>
      <style>{`
        .vim-wrap .ant-modal { padding: 0 !important; }
        .vim-wrap.vim-max .ant-modal {
          top: 0 !important;
          margin: 0 !important;
          max-width: 100vw !important;
          width: 100vw !important;
          height: 100vh !important;
        }
        .vim-wrap .ant-modal-content {
          height: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          padding: 0 !important;
        }
        .vim-wrap.vim-max .ant-modal-content {
          border-radius: 0 !important;
        }
        .vim-wrap .ant-modal-header {
          padding: 12px 16px !important;
          margin-bottom: 0 !important;
          border-bottom: 1px solid rgba(5,5,5,0.06);
        }
        .vim-wrap .ant-modal-body {
          flex: 1 !important;
          overflow: hidden !important;
          padding: 12px !important;
        }
      `}</style>
      <Modal
        wrapClassName={`vim-wrap${isMaximized ? ' vim-max' : ''}`}
        title={
          <div
            onMouseDown={handleTitleMouseDown}
            style={{
              cursor: isMaximized ? 'default' : 'move',
              userSelect: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{`检测与跟踪控制 - ${streamType === 'vis' ? '可见光' : '红外'}`}</span>
            <Space size={4} onMouseDown={(e) => e.stopPropagation()}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setIsMaximized((prev) => !prev)}
                title={isMaximized ? '恢复为窗口' : '最大化'}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
                title="关闭"
              />
            </Space>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width={isMaximized ? '100vw' : windowSize.width}
        style={{
          top: isMaximized ? 0 : 20,
          padding: 0,
          maxWidth: 'none',
        }}
        footer={null}
        closable={false}
        forceRender
        mask={false}
        maskClosable={false}
        modalRender={(node) => (
          <div
            style={{
              position: 'relative',
              pointerEvents: 'auto',
              width: isMaximized ? '100vw' : windowSize.width,
              height: isMaximized ? '100vh' : windowSize.height,
              transform: !isMaximized ? `translate(${modalOffset.x}px, ${modalOffset.y}px)` : undefined,
            }}
          >
            {node}
            {!isMaximized && (
              <>
                {/* 4 个边的 resize 手柄 */}
                <div onMouseDown={startResize('n')} style={{ position: 'absolute', top: -3, left: 8, right: 8, height: 8, cursor: 'ns-resize', zIndex: 10001, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('s')} style={{ position: 'absolute', bottom: -3, left: 8, right: 8, height: 8, cursor: 'ns-resize', zIndex: 10001, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('e')} style={{ position: 'absolute', right: -3, top: 8, bottom: 8, width: 8, cursor: 'ew-resize', zIndex: 10001, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('w')} style={{ position: 'absolute', left: -3, top: 8, bottom: 8, width: 8, cursor: 'ew-resize', zIndex: 10001, pointerEvents: 'auto' }} />
                {/* 4 个角的 resize 手柄 */}
                <div onMouseDown={startResize('nw')} style={{ position: 'absolute', top: -3, left: -3, width: 14, height: 14, cursor: 'nwse-resize', zIndex: 10002, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('ne')} style={{ position: 'absolute', top: -3, right: -3, width: 14, height: 14, cursor: 'nesw-resize', zIndex: 10002, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('sw')} style={{ position: 'absolute', bottom: -3, left: -3, width: 14, height: 14, cursor: 'nesw-resize', zIndex: 10002, pointerEvents: 'auto' }} />
                <div onMouseDown={startResize('se')} style={{ position: 'absolute', bottom: -3, right: -3, width: 14, height: 14, cursor: 'nwse-resize', zIndex: 10002, pointerEvents: 'auto' }} />
              </>
            )}
          </div>
        )}
      >
        <Row gutter={16} style={{ height: '100%' }}>
        {/* 左侧：视频区域 */}
        <Col span={17} style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            ref={videoContainerRef}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16 / 9',
              maxHeight: '100%',
              backgroundColor: '#000',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {/* 共享的播放器宿主挂载位置（Modal 打开时由 callback ref 接管） */}
            <div
              ref={setPlayerSlotRef}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}
            />

            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 2,
                cursor: getCursor(),
                pointerEvents: canvasInteractive() ? 'auto' : 'none',
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onContextMenu={handleContextMenu}
            />
          </div>
        </Col>

        {/* 右侧：控制面板 */}
        <Col span={7} style={{ height: '100%', overflowY: 'auto' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* 工作模式切换 */}
            <Card title="工作模式" size="small">
              <Radio.Group
                value={interactionMode}
                onChange={(e) => setInteractionMode(e.target.value)}
                buttonStyle="solid"
                style={{ width: '100%' }}
              >
                <Radio.Button value="view" style={{ width: '33.3%', textAlign: 'center' }}>
                  查看
                </Radio.Button>
                <Radio.Button value="track" style={{ width: '33.3%', textAlign: 'center' }}>
                  跟踪
                </Radio.Button>
                <Radio.Button value="detect" style={{ width: '33.4%', textAlign: 'center' }}>
                  检测
                </Radio.Button>
              </Radio.Group>
              <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                {interactionMode === 'view' && '查看模式：中心十字微调'}
                {interactionMode === 'track' && '跟踪模式：设置跟踪点，配置跟踪参数'}
                {interactionMode === 'detect' && '检测模式：配置检测参数和检测区域'}
              </Text>
            </Card>

            {interactionMode === 'view' && (
              <Card title="中心十字微调" size="small">
                <Space style={{ marginBottom: 8 }}>
                  <Text>步长</Text>
                  <InputNumber min={1} max={1000} value={centerCrossStep} onChange={(v) => setCenterCrossStep(v || 1)} />
                  <Button type="primary" onClick={saveCenterCross}>确定</Button>
                </Space>
                <div style={{ display: 'flex', justifyContent: 'center', minHeight: 132 }}>
                  <div className="ptz-controller" style={{ transform: 'scale(0.65)' }}>
                    <Button
                      aria-label="中心十字上移1像素"
                      className="ptz-button"
                      style={{ top: 10, left: '50%', transform: 'translateX(-50%)' }}
                      icon={<UpOutlined />}
                      onClick={() => handleCenterCrossMove(0)}
                    />
                    <Button
                      aria-label="中心十字下移1像素"
                      className="ptz-button"
                      style={{ bottom: 10, left: '50%', transform: 'translateX(-50%)' }}
                      icon={<DownOutlined />}
                      onClick={() => handleCenterCrossMove(1)}
                    />
                    <Button
                      aria-label="中心十字左移1像素"
                      className="ptz-button"
                      style={{ left: 10, top: '50%', transform: 'translateY(-50%)' }}
                      icon={<LeftOutlined />}
                      onClick={() => handleCenterCrossMove(2)}
                    />
                    <Button
                      aria-label="中心十字右移1像素"
                      className="ptz-button"
                      style={{ right: 10, top: '50%', transform: 'translateY(-50%)' }}
                      icon={<RightOutlined />}
                      onClick={() => handleCenterCrossMove(3)}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 40,
                        height: 40,
                        background: '#f0f2f5',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AimOutlined style={{ fontSize: 18, color: '#999' }} />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* ============================================ */}
            {/* 跟踪模式控制 */}
            {/* ============================================ */}
            {interactionMode === 'track' && (
              <>
                <Card title="跟踪控制" size="small">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {/* 跟踪模式选择 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>跟踪模式:</Text>
                      <Select
                        size="small"
                        value={trackMode}
                        onChange={setTrackMode}
                        style={{ width: 120 }}
                        options={[
                          { value: 0, label: '关闭' },
                          { value: 1, label: '像素点', disabled: true },
                          { value: 2, label: '识别' },
                          { value: 3, label: '目标ID', disabled: true },
                        ]}
                      />
                    </div>

                    {/* track_plot: 独立控制，修改立即发送 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>绘制跟踪结果:</Text>
                      <Switch
                        size="small"
                        checked={trackPlot === 1}
                        onChange={handleTrackPlotChange}
                      />
                    </div>

                    {/* 跟踪点显示 (仅 trackMode=1) */}
                    {trackMode === 1 && (
                      <div style={rowStyle}>
                        <Text style={labelStyle}>跟踪点:</Text>
                        <Text style={{ fontSize: 12 }}>
                          {trackPoint ? `[${trackPoint[0]}, ${trackPoint[1]}]` : '点击画面设置'}
                        </Text>
                      </div>
                    )}

                    {/* 目标ID (仅 trackMode=3) */}
                    {trackMode === 3 && (
                      <div style={rowStyle}>
                        <Text style={labelStyle}>目标ID:</Text>
                        <Select
                          size="small"
                          value={trackId}
                          onChange={setTrackId}
                          style={{ width: 80 }}
                          options={[
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' },
                          ]}
                        />
                      </div>
                    )}

                    {/* 波门大小 (trackMode=1 时可选) */}
                    {trackMode === 1 && (
                      <div style={rowStyle}>
                        <Text style={labelStyle}>波门大小:</Text>
                        <Select
                          size="small"
                          value={gateSize}
                          onChange={setGateSize}
                          style={{ width: 100 }}
                          options={[
                            { value: 0, label: '自适应' },
                            { value: 1, label: '小' },
                            { value: 2, label: '中' },
                            { value: 3, label: '大' },
                          ]}
                        />
                      </div>
                    )}

                    {/* 波门移动: 独立控制 */}
                    <Divider style={{ margin: '8px 0' }}>波门移动</Divider>
                    <div style={rowStyle}>
                      <Text style={labelStyle}>步长:</Text>
                      <InputNumber
                        size="small"
                        min={1}
                        max={100}
                        value={gateStep}
                        onChange={(v) => setGateStep(v || 5)}
                        style={{ width: 80 }}
                        addonAfter="px"
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <Button
                        size="small"
                        icon={<UpOutlined />}
                        onClick={() => handleGateMove(0)}
                        style={{ width: 40 }}
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button
                          size="small"
                          icon={<LeftOutlined />}
                          onClick={() => handleGateMove(2)}
                          style={{ width: 40 }}
                        />
                        <Button
                          size="small"
                          icon={<DownOutlined />}
                          onClick={() => handleGateMove(1)}
                          style={{ width: 40 }}
                        />
                        <Button
                          size="small"
                          icon={<RightOutlined />}
                          onClick={() => handleGateMove(3)}
                          style={{ width: 40 }}
                        />
                      </div>
                    </div>

                    {trackPoint && trackMode === 1 && (
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setTrackPoint(null)}
                        block
                        style={{ marginTop: 4 }}
                      >
                        清除跟踪点
                      </Button>
                    )}
                  </Space>
                </Card>

                {/* 跟踪操作按钮 */}
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button icon={<UndoOutlined />} onClick={handleReset}>
                    重置
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleTrackSend}
                    disabled={trackOptionsDisabled && trackMode !== 0 && trackMode !== 2}
                  >
                    发送
                  </Button>
                </Space>
              </>
            )}

            {/* ============================================ */}
            {/* 检测模式控制 */}
            {/* ============================================ */}
            {interactionMode === 'detect' && (
              <>
                <Card title="检测设置" size="small">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {/* 检测开关 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>检测开关:</Text>
                      <Radio.Group
                        size="small"
                        value={detectEn}
                        onChange={(e) => setDetectEn(e.target.value)}
                        buttonStyle="solid"
                      >
                        <Radio.Button value={0}>关闭</Radio.Button>
                        <Radio.Button value={1}>切片检测</Radio.Button>
                        <Radio.Button value={2}>全图检测</Radio.Button>
                      </Radio.Group>
                    </div>

                    {/* 检测类型 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>检测类型:</Text>
                      <Checkbox.Group
                        value={detectType}
                        onChange={(v) => setDetectType(v as string[])}
                        disabled={detectEn === 0}
                        options={[
                          { label: '无人机', value: 'drone' },
                        ]}
                      />
                    </div>

                    {/* 置信度 */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={rowStyle}>
                        <Text style={labelStyle}>置信度:</Text>
                        <Text style={{ fontSize: 12, color: '#1890ff' }}>{detectConf}</Text>
                      </div>
                      <Slider
                        min={10}
                        max={100}
                        value={detectConf}
                        onChange={setDetectConf}
                        disabled={detectEn === 0}
                      />
                    </div>

                    {/* 面积过滤 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>最小面积:</Text>
                      <InputNumber
                        size="small"
                        min={1}
                        max={10000}
                        precision={0}
                        value={smallArea}
                        onChange={(v) => setSmallArea(typeof v === 'number' ? v : 100)}
                        disabled={detectEn === 0}
                        style={{ width: 120 }}
                        addonAfter="px"
                      />
                    </div>

                    <div style={rowStyle}>
                      <Text style={labelStyle}>最大面积:</Text>
                      <InputNumber
                        size="small"
                        min={10000}
                        max={2000000}
                        precision={0}
                        value={bigArea}
                        onChange={(v) => setBigArea(typeof v === 'number' ? v : 100000)}
                        disabled={detectEn === 0}
                        style={{ width: 120 }}
                        addonAfter="px"
                      />
                    </div>

                    {/* AI模型 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>AI模型:</Text>
                      <Select
                        size="small"
                        value={modelName}
                        onChange={setModelName}
                        style={{ width: 140 }}
                        disabled={detectEn === 0}
                        options={[
                          { value: 'NORMAL', label: 'NORMAL（大小目标混训）' },
                          { value: 'SLICE_SMALL', label: 'SLICE_SMALL（仅小目标）' },
                        ]}
                      />
                    </div>

                    {/* 绘制检测结果 */}
                    <div style={rowStyle}>
                      <Text style={labelStyle}>绘制结果:</Text>
                      <Switch
                        size="small"
                        checked={detectPlot === 1}
                        onChange={(v) => setDetectPlot(v ? 1 : 0)}
                        disabled={detectEn === 0}
                      />
                    </div>
                  </Space>
                </Card>

                {/* ROI设置 */}
                <Card title="ROI设置" size="small">
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div style={rowStyle}>
                      <Text style={labelStyle}>ROI模式:</Text>
                      <Radio.Group
                        size="small"
                        value={roiMode}
                        onChange={(e) => setRoiMode(e.target.value)}
                        disabled={detectEn === 0}
                        buttonStyle="solid"
                      >
                        <Radio.Button value={0}>全图</Radio.Button>
                        <Radio.Button value={1}>窗口选取</Radio.Button>
                      </Radio.Group>
                    </div>

                    {roiMode === 1 && (
                      <>
                        <div style={rowStyle}>
                          <Text style={labelStyle}>当前区域:</Text>
                          <Text style={{ fontSize: 11 }}>
                            {roiRect
                              ? `[${roiRect.x1}, ${roiRect.y1}, ${roiRect.x2}, ${roiRect.y2}]`
                              : '未设置'}
                          </Text>
                        </div>

                        <Divider style={{ margin: '8px 0' }}>预设区域</Divider>

                        <Space wrap size="small">
                          <Button size="small" onClick={() => setPresetROI('center')} disabled={detectEn === 0}>
                            中心区域
                          </Button>
                          <Button size="small" onClick={() => setPresetROI('top')} disabled={detectEn === 0}>
                            上半部分
                          </Button>
                          <Button size="small" onClick={() => setPresetROI('bottom')} disabled={detectEn === 0}>
                            下半部分
                          </Button>
                          <Button size="small" onClick={() => setPresetROI('left')} disabled={detectEn === 0}>
                            左半部分
                          </Button>
                          <Button size="small" onClick={() => setPresetROI('right')} disabled={detectEn === 0}>
                            右半部分
                          </Button>
                          <Button size="small" onClick={() => setPresetROI('full')} disabled={detectEn === 0}>
                            全屏
                          </Button>
                        </Space>

                        {roiRect && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => setRoiRect(null)}
                            block
                            style={{ marginTop: 8 }}
                          >
                            清除ROI
                          </Button>
                        )}
                      </>
                    )}
                  </Space>
                </Card>

                {/* 检测操作按钮 */}
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button icon={<UndoOutlined />} onClick={handleReset}>
                    重置
                  </Button>
                  <Button type="primary" icon={<SendOutlined />} onClick={handleDetectSend}>
                    发送
                  </Button>
                </Space>
              </>
            )}
          </Space>
        </Col>
      </Row>
      </Modal>
    </>
  )
}

export default VideoInteractionModal
